/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Content, ContentType, ListAllContentResponse } from '../../../interface';
import { HibraryRootState } from '../../../redux/rootReducer';
import { BreadcrumbLink, BreadCrumbView } from '../../components/breadcrumb';
import { MemoContentCollectionView } from '../../components/contentCover';
import { EmptyDataView } from '../../components/nodata';
import { Loading } from '../../customUI/loading';
import { removeSplash } from '../../helpers/removeSplash';
import { action } from '../../redux/request';
import { getRentableContents } from '../../redux/store/storeCrud';
import { useIntl } from 'react-intl';
import './index.scss';

interface RentableContentsProps {
    contentType: ContentType;
}

const RentableContents = (props: RentableContentsProps) => {
    const { contentType } = props;
    const pageId = 'RentableContents';
    const dispatch = useDispatch();
    const intl = useIntl();
    const [contents, setContents] = useState<ListAllContentResponse>();
    const [breadCrumbs, setBreadCrumbs] = useState<BreadcrumbLink[]>();

    const { environment, clientInfo, authen, request } = useSelector(
        (state: HibraryRootState) => ({
            clientInfo: state.clientInfo,
            authen: state.auth,
            environment: state.environment,
            request: state.fetch
        }),
        shallowEqual
    );

    const breadcrumbs = useMemo(() => {
        return <BreadCrumbView addLinks={breadCrumbs} />;
    }, [breadCrumbs]);

    useEffect(() => {
        fetchRentableContents(false);
    }, []);

    useEffect(() => {
        if (environment.endOfScreen) {
            if (contents?.lastEvaluatedKey !== undefined && !request.loading) {
                fetchRentableContents(true);
            }
        }
    }, [environment]);

    const selectContentContainerClass = () => {
        switch (contentType) {
            case ContentType.ebook:
                return `content-list content-list-container-ebook `;
            case ContentType.video:
                return `content-list content-list-container-video `;
            case ContentType.podcast:
                return `content-list content-list-container-podcast `;
            default:
                return `content-list content-list-container-ebook `;
        }
    };

    const fetchRentableContents = async (isEndless: boolean) => {
        try {
            dispatch(action.setProcess(true, pageId));
            const lastEvaluatedKey = contents?.lastEvaluatedKey
                ? contents.lastEvaluatedKey
                : undefined;
            const response = await getRentableContents(
                authen.isLogin,
                clientInfo.companyPrefix,
                lastEvaluatedKey as string
            );
            if (response) {
                if (isEndless && contents?.contents) {
                    setContents({
                        ...contents,
                        contents: contents.contents.concat(response.contents),
                        lastEvaluatedKey: response.lastEvaluatedKey ?? undefined
                    });
                } else {
                    setContents(response);
                    setBreadCrumbs([
                        {
                            title: intl.formatMessage({
                                id: 'ContentType.BorrowingAvailable'
                            })
                        }
                    ]);
                }
            }
        } finally {
            dispatch(action.setProcess(false, pageId));
            dispatch(removeSplash);
        }
    };

    return (
        <>
            {breadcrumbs}
            <div className='view-all-content'>
                <h1 className='viewAllTitle'>
                    {intl.formatMessage({
                        id: 'ContentType.BorrowingAvailable'
                    })}
                </h1>
                {contents?.contents && contents.contents.length > 0 ? (
                    <div className={selectContentContainerClass()}>
                        {contents?.contents.map((el) => (
                            <MemoContentCollectionView
                                content={el as Content}
                                licenseType={el.licenseType}
                            />
                        ))}
                    </div>
                ) : (
                    !request.loading && <EmptyDataView />
                )}
            </div>
            <Loading id={pageId} />
        </>
    );
};

export default RentableContents;
